import { useMutation, useQueryClient } from "@tanstack/react-query";
import { grabsApi } from "@/api/grabs";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/components/ui/Toast";

/**
 * Per-row "Redownload" button for grab history tables.
 *
 * The button calls `POST /api/v1/grabs/:id/redownload`, which replays
 * the historical grab through whichever download client is currently
 * enabled. Built for the homelab recovery scenario: the embedded NZB
 * downloader truncated a bunch of releases (rar extraction bug), and
 * the operator switched to NZBGet — but the existing library entries
 * stay broken until each one is replayed.
 *
 * Confirms before firing because this creates a fresh download-client
 * job, hits the indexer's `t=get` endpoint, and writes a new row in
 * `grabs`. The original row is left as historical context.
 *
 * On success, invalidates the `grabs` query cache so the new row
 * shows up immediately.
 */
export function RedownloadButton({
  grabId,
  releaseTitle,
  size = "sm",
}: {
  grabId: number;
  releaseTitle: string;
  size?: "sm" | "md";
}) {
  const qc = useQueryClient();
  const { toast } = useToast();

  const mut = useMutation({
    mutationFn: () => grabsApi.redownload(grabId),
    onSuccess: (rec) => {
      toast(
        `Redownload dispatched → new grab #${rec.id} via ${rec.download_client_name ?? "?"}`,
        "success",
      );
      qc.invalidateQueries({ queryKey: ["grabs"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Redownload failed", "error"),
  });

  return (
    <Button
      variant="secondary"
      size={size}
      loading={mut.isPending}
      onClick={() => {
        if (
          confirm(
            `Redownload "${releaseTitle}"? This dispatches a fresh grab through whichever download client is currently enabled.`,
          )
        )
          mut.mutate();
      }}
    >
      Redownload
    </Button>
  );
}
